import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import {
  BarChartRounded,
  CheckCircleOutlineRounded,
  GraphicEqRounded,
  HeadphonesRounded,
  InfoOutlined,
  LightbulbOutlined,
  PauseRounded,
  PlayArrowRounded,
  QueryBuilderRounded,
  SpeedRounded,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useApiEp from "../../hooks/useApiEp";

const COLORS = {
  purple: "#7c3aed",
  green: "#20b86a",
  orange: "#f59e0b",
  blue: "#2878f0",
  pink: "#f43f75",
  text: "#101828",
  muted: "#667085",
  border: "#e5e7eb",
  surface: "#ffffff",
};

const BANDS = [
  { key: "above25", label: "Above 25 sec", color: COLORS.green },
  { key: "from20To25", label: "20 to 25 sec", color: COLORS.orange },
  { key: "from10To20", label: "10 to 20 sec", color: COLORS.blue },
  { key: "below10", label: "Below 10 sec", color: COLORS.pink },
];

const SUGGESTION_STYLES = {
  "Worst Clip": { color: "#b42318", background: "#fee4e2" },
  "Excellent Clip": { color: "#067647", background: "#dcfae6" },
  "Good Clip": { color: "#15803d", background: "#e8f8e9" },
  "Moderate Clip": { color: "#b54708", background: "#fef0c7" },
  "Needs Improvement": { color: "#b54708", background: "#ffead5" },
  "Poor Clip": { color: "#b42318", background: "#fee4e2" },
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatDuration = (value) => {
  const seconds = Math.round(Number(value) || 0);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

const clipName = (clip) =>
  clip?.fileName || clip?.originalFileName || `Clip ${clip?.clipId || ""}`;

const roleLabel = (role) =>
  ({
    welcomePId: "Welcome",
    menuPId: "Menu",
    noInputPId: "No input",
    wrongInputPId: "Wrong input",
    thanksPId: "Thank you",
  }[role] || "Voice clip");

const getSuggestion = (clip) => {
  if (clip?.suggestion) return clip.suggestion;
  const answered = Number(clip?.answeredCalls) || 0;
  if (!answered) return "Poor Clip";

  const bands = clip.listenBands || {};
  const below10Percent = ((bands.below10 || 0) / answered) * 100;
  const above25Percent = ((bands.above25 || 0) / answered) * 100;
  const above10Percent =
    (((bands.above25 || 0) +
      (bands.from20To25 || 0) +
      (bands.from10To20 || 0)) /
      answered) *
    100;

  if (below10Percent >= 60) return "Worst Clip";
  if (above25Percent >= 40) return "Excellent Clip";
  if (above10Percent >= 70) return "Good Clip";
  if (above10Percent >= 60) return "Moderate Clip";
  if (above10Percent >= 50) return "Needs Improvement";
  return "Poor Clip";
};

const Panel = ({ children, sx = {} }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      borderColor: COLORS.border,
      boxShadow: "0 2px 10px rgba(16, 24, 40, 0.035)",
      ...sx,
    }}
  >
    {children}
  </Card>
);

const SectionHeading = ({ title, subtitle, right }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    spacing={1}
    sx={{ px: 2.25, pt: 1.75, pb: 1.25 }}
  >
    <Box>
      <Typography sx={{ fontSize: 16, lineHeight: 1.25, fontWeight: 750 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: 0.2 }}>
        {subtitle}
      </Typography>
    </Box>
    {right}
  </Stack>
);

const ClipPlayer = ({ src }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const stop = () => setPlaying(false);
    audio.addEventListener("ended", stop);
    audio.addEventListener("pause", stop);
    return () => {
      audio.removeEventListener("ended", stop);
      audio.removeEventListener("pause", stop);
    };
  }, [src]);

  const toggle = (event) => {
    event.stopPropagation();
    if (!audioRef.current || !src) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying((current) => !current);
  };

  return (
    <Box
      sx={{
        width: 82,
        height: 34,
        borderRadius: 5,
        bgcolor: "#f8fafc",
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        px: 0.5,
        flexShrink: 0,
      }}
    >
      {src && <audio ref={audioRef} src={src} preload="none" />}
      <MuiTooltip title={src ? (playing ? "Pause clip" : "Play clip") : "Audio unavailable"}>
        <span>
          <IconButton size="small" disabled={!src} onClick={toggle}>
            {playing ? <PauseRounded fontSize="small" /> : <PlayArrowRounded fontSize="small" />}
          </IconButton>
        </span>
      </MuiTooltip>
      <Box sx={{ width: 20, height: 3, borderRadius: 2, bgcolor: "#cbd5e1" }} />
      <GraphicEqRounded sx={{ fontSize: 18, color: "#64748b" }} />
    </Box>
  );
};

const SummaryCard = ({ label, value, helper, color, background, icon }) => (
  <Panel sx={{ height: "100%" }}>
    <Stack direction="row" spacing={1.2} sx={{ p: 1.7 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: background,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, color: COLORS.muted, fontWeight: 650 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 23, lineHeight: 1.25, fontWeight: 780 }}>
          {formatNumber(value)}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: COLORS.muted, whiteSpace: "nowrap" }}>
          {helper}
        </Typography>
      </Box>
    </Stack>
  </Panel>
);

const SuggestionChip = ({ suggestion }) => {
  const style = SUGGESTION_STYLES[suggestion] || SUGGESTION_STYLES["Poor Clip"];
  return (
    <Chip
      size="small"
      label={suggestion}
      sx={{
        minWidth: 118,
        height: 24,
        fontSize: 10.5,
        fontWeight: 700,
        color: style.color,
        bgcolor: style.background,
      }}
    />
  );
};

const VoiceClipAnalysis = () => {
  const [selectedClipId, setSelectedClipId] = useState("");
  const { data: result = {}, isLoading, isError } = useApiEp(
    "/voice/get-voice-clips-stats"
  );
  const rows = useMemo(
    () => (Array.isArray(result.data) ? result.data : []),
    [result.data]
  );
  const selectedClip =
    rows.find((row) => String(row.clipId) === selectedClipId) || rows[0] || {};
  const selectedBands = selectedClip.listenBands || {};

  const chartData = rows.map((row) => ({
    name: clipName(row),
    ...row.listenBands,
  }));

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        color: COLORS.text,
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: { xs: 23, md: 27 }, fontWeight: 800 }}>
          All voice clip analysis
        </Typography>
        <Typography sx={{ fontSize: 13, color: COLORS.muted, mt: 0.2 }}>
          Compare listening-duration counts and clip suggestions for all voice clips.
        </Typography>
      </Box>

      <Panel sx={{ mb: 1.5 }}>
        <SectionHeading
          title="Clip performance"
          subtitle="Completion is estimated when an answered call lasts at least as long as the clip. Select a row to update the cards below."
        />
        <TableContainer sx={{ px: 1, pb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Voice clip", "Clip length", "Answered", "Unique listeners", "Avg. listened", "Completed"].map((label) => (
                  <TableCell
                    key={label}
                    sx={{
                      bgcolor: "#f8fafc",
                      borderTop: `1px solid ${COLORS.border}`,
                      color: "#344054",
                      fontSize: 11,
                      fontWeight: 750,
                      textTransform: "uppercase",
                      letterSpacing: 0.25,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row) => {
                  const selected = String(row.clipId) === String(selectedClip.clipId);
                  return (
                    <TableRow
                      key={row.clipId}
                      hover
                      selected={selected}
                      onClick={() => setSelectedClipId(String(row.clipId))}
                      sx={{ cursor: "pointer", "&.Mui-selected": { bgcolor: "#faf9ff" } }}
                    >
                      <TableCell sx={{ minWidth: 260 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <ClipPlayer src={row.voiceFileUrl} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ maxWidth: 220, fontSize: 12.5, fontWeight: 750 }}>
                              {clipName(row)}
                            </Typography>
                            <Stack direction="row" spacing={0.4} sx={{ mt: 0.4 }}>
                              {(row.roles || []).map((role) => (
                                <Chip key={role} size="small" label={roleLabel(role)} sx={{ height: 18, fontSize: 9.5 }} />
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{formatDuration(row.clipDuration)}</TableCell>
                      <TableCell>{formatNumber(row.answeredCalls)}</TableCell>
                      <TableCell>{formatNumber(row.uniqueListeners)}</TableCell>
                      <TableCell>{formatDuration(row.avgListenDuration)}</TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <Stack spacing={0.55}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{formatNumber(row.estimatedCompleted)}</Typography>
                            <Typography sx={{ fontSize: 10.5, color: COLORS.muted }}>{row.completionRate || 0}%</Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(100, Number(row.completionRate) || 0)}
                            sx={{ height: 6, borderRadius: 4, bgcolor: "#edf0f4", "& .MuiLinearProgress-bar": { bgcolor: "#159c8c", borderRadius: 4 } }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: COLORS.muted }}>
                    {isError ? "Could not load voice clip analysis." : "No voice clip activity found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="Answered" value={selectedClip.answeredCalls} helper="Total answered calls" color="#16a34a" background="#dcfce7" icon={<HeadphonesRounded />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="Estimated completions" value={selectedClip.estimatedCompleted} helper="Estimated full listens" color={COLORS.purple} background="#f3e8ff" icon={<CheckCircleOutlineRounded />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="Above 25 sec listened" value={selectedBands.above25} helper="Long listens" color={COLORS.green} background="#dcfae6" icon={<BarChartRounded />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="20 to 25 sec" value={selectedBands.from20To25} helper="Mid-high listens" color={COLORS.orange} background="#fff4dc" icon={<QueryBuilderRounded />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="10 to 20 sec" value={selectedBands.from10To20} helper="Mid listens" color={COLORS.blue} background="#e6f0ff" icon={<SpeedRounded />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <SummaryCard label="Below 10 sec" value={selectedBands.below10} helper="Early drop-offs" color={COLORS.pink} background="#ffe5ee" icon={<QueryBuilderRounded />} />
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6.3}>
          <Panel sx={{ height: "100%" }}>
            <SectionHeading title="All clips by listeners" subtitle="Listening-duration count distribution for each voice clip." />
            <Box sx={{ width: "100%", height: Math.max(300, rows.length * 78), px: 1, pb: 1.5 }}>
              {rows.length ? (
                <ResponsiveContainer>
                  <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 25, bottom: 8, left: 15 }} barGap={1}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e8edf3" />
                    <XAxis type="number" allowDecimals={false} label={{ value: "Count", position: "insideBottom", offset: -4, fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={108} tick={{ fontSize: 11 }} tickFormatter={(value) => value.length > 16 ? `${value.slice(0, 16)}…` : value} />
                    <Tooltip formatter={(value, name) => [formatNumber(value), BANDS.find((band) => band.key === name)?.label || name]} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} formatter={(value) => BANDS.find((band) => band.key === value)?.label || value} />
                    {BANDS.map((band) => (
                      <Bar key={band.key} dataKey={band.key} fill={band.color} radius={[0, 3, 3, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center" }}>
                  <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>No listener data available.</Typography>
                </Box>
              )}
            </Box>
          </Panel>
        </Grid>

        <Grid item xs={12} lg={5.7}>
          <Stack spacing={1.5} sx={{ height: "100%" }}>
            <Panel>
              <SectionHeading title="Clip suggestions" subtitle="Count-based clip summary and recommendation for each clip." />
              <TableContainer sx={{ px: 1, pb: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Clip name", "Above 25 sec", "20 to 25 sec", "10 to 20 sec", "Below 10 sec", "Suggestion"].map((label) => (
                        <TableCell key={label} sx={{ bgcolor: "#f8fafc", fontSize: 10.5, fontWeight: 750, color: "#344054", whiteSpace: "nowrap" }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.clipId} hover onClick={() => setSelectedClipId(String(row.clipId))} sx={{ cursor: "pointer" }}>
                        <TableCell sx={{ maxWidth: 140 }}><Typography noWrap sx={{ fontSize: 11.5, fontWeight: 650 }}>{clipName(row)}</Typography></TableCell>
                        <TableCell align="center">{formatNumber(row.listenBands?.above25)}</TableCell>
                        <TableCell align="center">{formatNumber(row.listenBands?.from20To25)}</TableCell>
                        <TableCell align="center">{formatNumber(row.listenBands?.from10To20)}</TableCell>
                        <TableCell align="center">{formatNumber(row.listenBands?.below10)}</TableCell>
                        <TableCell align="center"><SuggestionChip suggestion={getSuggestion(row)} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>

            <Panel sx={{ bgcolor: "#fbf9ff", borderColor: "#e9ddff", flex: 1 }}>
              <Stack direction="row" spacing={1.4} sx={{ p: 2 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "#f1e8ff", color: COLORS.purple, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <LightbulbOutlined />
                </Box>
                <Box>
                  <Typography sx={{ color: "#4c1d95", fontSize: 13, fontWeight: 750, mb: 0.6 }}>Suggestion logic</Typography>
                  <Grid container spacing={0.4}>
                    {[
                      "Below 10 sec ≥ 60% → Worst Clip",
                      "Above 25 sec ≥ 40% → Excellent Clip",
                      "Above 10 sec ≥ 70% → Good Clip",
                      "Above 10 sec 60–69.99% → Moderate Clip",
                      "Above 10 sec 50–59.99% → Needs Improvement",
                      "Above 10 sec < 50% → Poor Clip",
                    ].map((text) => (
                      <Grid item xs={12} md={6} key={text}>
                        <Typography sx={{ fontSize: 10.5, lineHeight: 1.45, color: "#475467" }}>• {text}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Panel>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mt: 1.5, px: 2, py: 1.15, borderRadius: 2.5, color: "#175cd3", bgcolor: "#eff8ff" }}>
        <InfoOutlined sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 750 }}>Count-based view</Typography>
        <Typography sx={{ fontSize: 11.5, color: "#475467" }}>
          This view shows counts only. Suggestions are based on the relationship between long listens and early drop-offs.
        </Typography>
      </Stack>
    </Box>
  );
};

export default VoiceClipAnalysis;
